import * as z from "zod";
import {
  AnalyticsBooleanAggregateOps,
  AnalyticsCollectAggregateOps,
  AnalyticsCountAggregateOps,
  AnalyticsFractionAggregateOps,
  AnalyticsOrderedAggregateOps,
  AnalyticsPlainAggregateOps,
  AnalyticsRunningSummaryOps,
} from "./analytics-query-aggregate-types";
import { analyticsConstantArgumentProblem } from "./analytics-query-constant-arguments";
import {
  ANALYTICS_QUERY_LIMITS,
  AnalyticsDayOrCoarserUnitSchema,
  AnalyticsDirectionSchema,
  AnalyticsNameKind,
  AnalyticsNodeKind,
  AnalyticsPositionFractionSchema,
  AnalyticsRelativeOp,
  AnalyticsScope,
  AnalyticsSemanticType,
  AnalyticsTiePolicySchema,
  AnalyticsTimeUnitSchema,
} from "./analytics-query-constants";
import type {
  AnalyticsAggregateNode,
  AnalyticsBooleanAggregateNode,
  AnalyticsBooleanConnectiveCallNode,
  AnalyticsBooleanConstantNode,
  AnalyticsBooleanExpression,
  AnalyticsBooleanPredicateCallNode,
  AnalyticsCallNode,
  AnalyticsCategorizeNode,
  AnalyticsConstantNode,
  AnalyticsExpression,
  AnalyticsPer,
  AnalyticsRefNode,
  AnalyticsRelativeNode,
  AnalyticsSequence,
} from "./analytics-query-expression-types";
import { ANALYTICS_FUNCTION_NAMES } from "./analytics-query-function-registry";
import {
  ANALYTICS_ANY_UNIT_BINARY_FUNCTION_NAMES,
  ANALYTICS_ANY_UNIT_UNARY_FUNCTION_NAMES,
  ANALYTICS_BINARY_FUNCTION_NAMES,
  ANALYTICS_BINARY_PREDICATE_NAMES,
  ANALYTICS_DAY_UNARY_FUNCTION_NAMES,
  ANALYTICS_NULLARY_FUNCTION_NAMES,
  ANALYTICS_QUINARY_FUNCTION_NAMES,
  ANALYTICS_TERNARY_FUNCTION_NAMES,
  ANALYTICS_TERNARY_PREDICATE_NAMES,
  ANALYTICS_UNARY_BOOLEAN_FUNCTION_NAMES,
  ANALYTICS_UNARY_FUNCTION_NAMES,
  ANALYTICS_UNARY_PREDICATE_NAMES,
  ANALYTICS_VARIADIC_BOOLEAN_FUNCTION_NAMES,
  ANALYTICS_VARIADIC_FUNCTION_NAMES,
  ANALYTICS_VARIADIC_PAIR_FUNCTION_NAMES,
  ANALYTICS_VARIADIC_PAIR_PREDICATE_NAMES,
} from "./analytics-query-function-signatures";
import type { AnalyticsFunctionName } from "./analytics-query-function-types";
import {
  AnalyticsConstantValueSchema,
  AnalyticsNeighborAtSchema,
  AnalyticsSpanSchema,
} from "./analytics-query-primitive-schemas";
import {
  ANALYTICS_LOCAL_ROUTE_MESSAGE,
  AnalyticsEntityNameSchema,
  AnalyticsRouteNameSchema,
  AnalyticsValueNameSchema,
} from "./analytics-query-refs";

/**
 * Structural schema of the expression: one recursive discriminated union and
 * its arms. It answers "is this tree well-formed?" and nothing else. It
 * imports no catalog: a name is a branded shape whose existence, type and
 * grain the resolution layer checks. Every discriminant is read from a constant.
 *
 * The union is declared before its arms so the arms can reference it
 * directly; `z.lazy` defers the union's own construction until parse time.
 */
export const AnalyticsExpressionSchema: z.ZodType<AnalyticsExpression> = z.lazy(
  () =>
    z.discriminatedUnion("kind", [
      AnalyticsRefNodeSchema,
      AnalyticsConstantNodeSchema,
      AnalyticsCallNodeSchema,
      AnalyticsCategorizeNodeSchema,
      AnalyticsAggregateNodeSchema,
      AnalyticsRelativeNodeSchema,
    ]),
);

export const AnalyticsBooleanExpressionSchema: z.ZodType<AnalyticsBooleanExpression> =
  z.lazy(() =>
    z.union([
      AnalyticsRefNodeSchema,
      AnalyticsBooleanConstantNodeSchema,
      AnalyticsBooleanPredicateCallNodeSchema,
      AnalyticsBooleanConnectiveCallNodeSchema,
      AnalyticsBooleanAggregateNodeSchema,
    ]),
  );

/** A route picks a catalog path; a local definition has already chosen its own. */
export const AnalyticsRefNodeSchema = z
  .strictObject({
    kind: z.literal(AnalyticsNodeKind.Ref),
    name: AnalyticsValueNameSchema,
    route: AnalyticsRouteNameSchema.optional(),
  })
  .superRefine((node, ctx) => {
    if (
      node.route !== undefined &&
      node.name.kind !== AnalyticsNameKind.Catalog
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["route"],
        message: ANALYTICS_LOCAL_ROUTE_MESSAGE,
        input: node.route,
      });
    }
  }) satisfies z.ZodType<AnalyticsRefNode>;

export const AnalyticsConstantNodeSchema = z.strictObject({
  kind: z.literal(AnalyticsNodeKind.Constant),
  value: AnalyticsConstantValueSchema,
}) satisfies z.ZodType<AnalyticsConstantNode>;

export const AnalyticsFunctionNameSchema = z.enum(ANALYTICS_FUNCTION_NAMES);

const callKind = { kind: z.literal(AnalyticsNodeKind.Call) } as const;

/** A parameter the registry marks constant takes a constant node; JSON Schema has no positional slots to say so. */
const constantArguments = (
  node: {
    readonly fn: AnalyticsFunctionName;
    readonly args: readonly AnalyticsExpression[];
  },
  ctx: z.RefinementCtx,
): void => {
  for (const [index, arg] of node.args.entries()) {
    const message = analyticsConstantArgumentProblem(node.fn, index, arg);
    if (message !== undefined) {
      ctx.addIssue({
        code: "custom",
        path: ["args", index],
        message,
        input: arg,
      });
    }
  }
};

/** One arm per argument shape, discriminated on `fn`, so count and unit reach JSON Schema. */
const analyticsNullaryCall = z
  .strictObject({
    ...callKind,
    fn: z.enum(ANALYTICS_NULLARY_FUNCTION_NAMES),
    args: z.array(AnalyticsExpressionSchema).min(0).max(0),
  })
  .superRefine(constantArguments);
const analyticsUnaryCall = z
  .strictObject({
    ...callKind,
    fn: z.enum(ANALYTICS_UNARY_FUNCTION_NAMES),
    args: z.array(AnalyticsExpressionSchema).min(1).max(1),
  })
  .superRefine(constantArguments);
const analyticsUnaryBooleanCall = z
  .strictObject({
    ...callKind,
    fn: z.enum(ANALYTICS_UNARY_BOOLEAN_FUNCTION_NAMES),
    args: z.array(AnalyticsBooleanExpressionSchema).min(1).max(1),
  })
  .superRefine(constantArguments);
const analyticsBinaryCall = z
  .strictObject({
    ...callKind,
    fn: z.enum(ANALYTICS_BINARY_FUNCTION_NAMES),
    args: z.array(AnalyticsExpressionSchema).min(2).max(2),
  })
  .superRefine(constantArguments);
const analyticsTernaryCall = z
  .strictObject({
    ...callKind,
    fn: z.enum(ANALYTICS_TERNARY_FUNCTION_NAMES),
    args: z.array(AnalyticsExpressionSchema).min(3).max(3),
  })
  .superRefine(constantArguments);
const analyticsQuinaryCall = z
  .strictObject({
    ...callKind,
    fn: z.enum(ANALYTICS_QUINARY_FUNCTION_NAMES),
    args: z.array(AnalyticsExpressionSchema).min(5).max(5),
  })
  .superRefine(constantArguments);
const analyticsVariadicCall = z
  .strictObject({
    ...callKind,
    fn: z.enum(ANALYTICS_VARIADIC_FUNCTION_NAMES),
    args: z
      .array(AnalyticsExpressionSchema)
      .min(1)
      .max(ANALYTICS_QUERY_LIMITS.argsMax),
  })
  .superRefine(constantArguments);
const analyticsVariadicPairCall = z
  .strictObject({
    ...callKind,
    fn: z.enum(ANALYTICS_VARIADIC_PAIR_FUNCTION_NAMES),
    args: z
      .array(AnalyticsExpressionSchema)
      .min(2)
      .max(ANALYTICS_QUERY_LIMITS.argsMax),
  })
  .superRefine(constantArguments);
const analyticsVariadicBooleanCall = z
  .strictObject({
    ...callKind,
    fn: z.enum(ANALYTICS_VARIADIC_BOOLEAN_FUNCTION_NAMES),
    args: z
      .array(AnalyticsBooleanExpressionSchema)
      .min(1)
      .max(ANALYTICS_QUERY_LIMITS.argsMax),
  })
  .superRefine(constantArguments);
const analyticsDayUnaryCall = z
  .strictObject({
    ...callKind,
    fn: z.enum(ANALYTICS_DAY_UNARY_FUNCTION_NAMES),
    args: z.array(AnalyticsExpressionSchema).min(1).max(1),
    unit: AnalyticsDayOrCoarserUnitSchema,
  })
  .superRefine(constantArguments);
const analyticsAnyUnitUnaryCall = z
  .strictObject({
    ...callKind,
    fn: z.enum(ANALYTICS_ANY_UNIT_UNARY_FUNCTION_NAMES),
    args: z.array(AnalyticsExpressionSchema).min(1).max(1),
    unit: AnalyticsTimeUnitSchema,
  })
  .superRefine(constantArguments);
const analyticsAnyUnitBinaryCall = z
  .strictObject({
    ...callKind,
    fn: z.enum(ANALYTICS_ANY_UNIT_BINARY_FUNCTION_NAMES),
    args: z.array(AnalyticsExpressionSchema).min(2).max(2),
    unit: AnalyticsTimeUnitSchema,
  })
  .superRefine(constantArguments);

export const AnalyticsCallNodeSchema = z.discriminatedUnion("fn", [
  analyticsNullaryCall,
  analyticsUnaryCall,
  analyticsUnaryBooleanCall,
  analyticsBinaryCall,
  analyticsTernaryCall,
  analyticsQuinaryCall,
  analyticsVariadicCall,
  analyticsVariadicPairCall,
  analyticsVariadicBooleanCall,
  analyticsDayUnaryCall,
  analyticsAnyUnitUnaryCall,
  analyticsAnyUnitBinaryCall,
]) satisfies z.ZodType<AnalyticsCallNode>;

const AnalyticsBooleanConstantNodeSchema = z.strictObject({
  kind: z.literal(AnalyticsNodeKind.Constant),
  value: z.strictObject({
    type: z.literal(AnalyticsSemanticType.Boolean),
    value: z.boolean(),
  }),
}) satisfies z.ZodType<AnalyticsBooleanConstantNode>;

const AnalyticsBooleanPredicateCallNodeSchema = z.discriminatedUnion("fn", [
  z
    .strictObject({
      ...callKind,
      fn: z.enum(ANALYTICS_UNARY_PREDICATE_NAMES),
      args: z.array(AnalyticsExpressionSchema).min(1).max(1),
    })
    .superRefine(constantArguments),
  z
    .strictObject({
      ...callKind,
      fn: z.enum(ANALYTICS_BINARY_PREDICATE_NAMES),
      args: z.array(AnalyticsExpressionSchema).min(2).max(2),
    })
    .superRefine(constantArguments),
  z
    .strictObject({
      ...callKind,
      fn: z.enum(ANALYTICS_TERNARY_PREDICATE_NAMES),
      args: z.array(AnalyticsExpressionSchema).min(3).max(3),
    })
    .superRefine(constantArguments),
  z
    .strictObject({
      ...callKind,
      fn: z.enum(ANALYTICS_VARIADIC_PAIR_PREDICATE_NAMES),
      args: z
        .array(AnalyticsExpressionSchema)
        .min(2)
        .max(ANALYTICS_QUERY_LIMITS.argsMax),
    })
    .superRefine(constantArguments),
]) satisfies z.ZodType<AnalyticsBooleanPredicateCallNode>;

const AnalyticsBooleanConnectiveCallNodeSchema = z.discriminatedUnion("fn", [
  analyticsUnaryBooleanCall,
  analyticsVariadicBooleanCall,
]) satisfies z.ZodType<AnalyticsBooleanConnectiveCallNode>;

const AnalyticsBooleanAggregateNodeSchema = z.strictObject({
  kind: z.literal(AnalyticsNodeKind.Aggregate),
  op: z.enum(AnalyticsBooleanAggregateOps),
  of: AnalyticsBooleanExpressionSchema,
  only: AnalyticsBooleanExpressionSchema.optional(),
}) satisfies z.ZodType<AnalyticsBooleanAggregateNode>;

const AnalyticsCategorizeCaseSchema = z.strictObject({
  when: AnalyticsBooleanExpressionSchema,
  value: AnalyticsExpressionSchema,
});

export const AnalyticsCategorizeNodeSchema = z.strictObject({
  kind: z.literal(AnalyticsNodeKind.Categorize),
  cases: z
    .array(AnalyticsCategorizeCaseSchema)
    .min(1)
    .max(ANALYTICS_QUERY_LIMITS.casesMax),
  otherwise: AnalyticsExpressionSchema,
}) satisfies z.ZodType<AnalyticsCategorizeNode>;

const AnalyticsSequenceTermSchema = z.strictObject({
  of: AnalyticsExpressionSchema,
  direction: AnalyticsDirectionSchema,
});

export const AnalyticsSequenceSchema: z.ZodType<AnalyticsSequence> = z
  .array(AnalyticsSequenceTermSchema)
  .min(1)
  .max(ANALYTICS_QUERY_LIMITS.sequenceMax);

export const AnalyticsPerSchema = z.union([
  z.strictObject({ entity: AnalyticsEntityNameSchema }),
  z.strictObject({
    keys: z
      .array(AnalyticsExpressionSchema)
      .max(ANALYTICS_QUERY_LIMITS.keysMax),
    as: z
      .array(AnalyticsValueNameSchema)
      .max(ANALYTICS_QUERY_LIMITS.keysMax)
      .optional(),
  }),
  z.strictObject({ rollup: z.literal(true) }),
]) satisfies z.ZodType<AnalyticsPer>;

const analyticsAggregateBase = {
  kind: z.literal(AnalyticsNodeKind.Aggregate),
  of: AnalyticsExpressionSchema,
  per: AnalyticsPerSchema.optional(),
  only: AnalyticsBooleanExpressionSchema.optional(),
  scope: z.literal(AnalyticsScope.All).optional(),
} as const;

export const AnalyticsAggregateNodeSchema = z.discriminatedUnion("op", [
  z.strictObject({
    ...analyticsAggregateBase,
    op: z.enum(AnalyticsPlainAggregateOps),
  }),
  z.strictObject({
    ...analyticsAggregateBase,
    op: z.enum(AnalyticsCountAggregateOps),
  }),
  z.strictObject({
    ...analyticsAggregateBase,
    op: z.enum(AnalyticsFractionAggregateOps),
    fraction: z.number().gt(0).lt(1),
  }),
  z.discriminatedUnion("distinct", [
    z.strictObject({
      ...analyticsAggregateBase,
      op: z.enum(AnalyticsCollectAggregateOps),
      // Not z.undefined(): the generation schema must convert to JSON Schema.
      distinct: z.literal(false).optional(),
      sequence: AnalyticsSequenceSchema.optional(),
    }),
    z.strictObject({
      ...analyticsAggregateBase,
      op: z.enum(AnalyticsCollectAggregateOps),
      distinct: z.literal(true),
      direction: AnalyticsDirectionSchema.optional(),
    }),
  ]),
  z.strictObject({
    ...analyticsAggregateBase,
    op: z.enum(AnalyticsOrderedAggregateOps),
    by: AnalyticsSequenceSchema,
  }),
]) satisfies z.ZodType<AnalyticsAggregateNode>;

const analyticsRelativeBase = {
  kind: z.literal(AnalyticsNodeKind.Relative),
  sequence: AnalyticsSequenceSchema,
  among: z
    .array(AnalyticsExpressionSchema)
    .min(1)
    .max(ANALYTICS_QUERY_LIMITS.amongMax)
    .optional(),
  only: AnalyticsBooleanExpressionSchema.optional(),
  scope: z.literal(AnalyticsScope.All).optional(),
} as const;

export const AnalyticsRelativeNodeSchema = z.discriminatedUnion("op", [
  z.strictObject({
    ...analyticsRelativeBase,
    op: z.literal(AnalyticsRelativeOp.Position),
    ties: AnalyticsTiePolicySchema,
  }),
  z.strictObject({
    ...analyticsRelativeBase,
    op: z.literal(AnalyticsRelativeOp.PositionFraction),
    of: AnalyticsPositionFractionSchema,
  }),
  z.strictObject({
    ...analyticsRelativeBase,
    op: z.literal(AnalyticsRelativeOp.Tile),
    count: z.int().min(2).max(ANALYTICS_QUERY_LIMITS.tilesMax),
  }),
  z.strictObject({
    ...analyticsRelativeBase,
    op: z.literal(AnalyticsRelativeOp.Neighbor),
    of: AnalyticsExpressionSchema,
    at: AnalyticsNeighborAtSchema,
    skipAbsent: z.literal(true).optional(),
    span: AnalyticsSpanSchema.optional(),
  }),
  z.strictObject({
    ...analyticsRelativeBase,
    op: z.literal(AnalyticsRelativeOp.Running),
    summary: z.enum(AnalyticsRunningSummaryOps),
    of: AnalyticsExpressionSchema,
    span: AnalyticsSpanSchema,
  }),
]) satisfies z.ZodType<AnalyticsRelativeNode>;
